/**
 * Utility functions for detecting webview context and handling mobile app redirects.
 */

type MobileHostWindow = Window & {
  ReactNativeWebView?: unknown;
  opera?: string;
  webkit?: {
    messageHandlers?: Record<string, unknown>;
  };
};

const APP_UA_TOKENS = ["JemaApp", "JemaServicesApp"];

const WEBVIEW_PATTERNS = [
  /\bwv\b/i, // Android WebView token
  /;\s*wv\)/i, // Android WebView UA segment
  /WebView/i,
  /(iPhone|iPod|iPad)(?!.*Safari\/)/i, // iOS webview user agent
  /Android.*(?:wv|\.0\.0\.0)/i, // Android WebView indicators
];

const hasAppQueryHint = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("app") === "1" ||
    params.get("webview") === "1" ||
    params.get("platform") === "app"
  );
};

const isMobileDevice = (): boolean => {
  const nav = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };

  if (typeof nav.userAgentData?.mobile === "boolean") {
    return nav.userAgentData.mobile;
  }

  const userAgent = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(userAgent);
};

/**
 * Detects if the current context is a mobile webview.
 */
export function isWebView(): boolean {
  if (typeof window === "undefined") return false;

  const hostWindow = window as MobileHostWindow;
  const userAgent = navigator.userAgent || navigator.vendor || hostWindow.opera || "";

  return WEBVIEW_PATTERNS.some((pattern) => pattern.test(userAgent));
}

/**
 * Detects if the current context is a mobile app host (webview/native wrapper).
 */
export function isMobileApp(): boolean {
  if (typeof window === "undefined") return false;

  const hostWindow = window as MobileHostWindow;
  const userAgent = navigator.userAgent || "";

  const hasHostBridge =
    hostWindow.ReactNativeWebView !== undefined ||
    hostWindow.webkit?.messageHandlers !== undefined;

  const hasAppIdentifier = APP_UA_TOKENS.some((token) => userAgent.includes(token));

  return isWebView() || hasHostBridge || hasAppIdentifier || hasAppQueryHint();
}

/**
 * Determines whether patient sign-in should use OTP flow.
 * Dynamic default:
 * - Mobile device => magic link
 * - Desktop => OTP
 * Can be overridden with VITE_PATIENT_AUTH_MODE=otp|magiclink.
 */
export function shouldUsePatientOtpAuth(): boolean {
  if (typeof window === "undefined") return false;

  const authMode = import.meta.env.VITE_PATIENT_AUTH_MODE;
  if (authMode === "otp") return true;
  if (authMode === "magiclink") return false;

  // Legacy override support
  if (import.meta.env.VITE_FORCE_PATIENT_OTP === "true") return true;
  if (import.meta.env.VITE_FORCE_PATIENT_OTP === "false") return false;

  return !isMobileDevice();
}

type MagicLinkRedirectOptions = {
  appRequested?: boolean;
};

/**
 * Gets the appropriate redirect URL for magic links.
 * Uses app URL if configured, otherwise falls back to current origin.
 */
export function getMagicLinkRedirectUrl(options?: MagicLinkRedirectOptions): string {
  if (typeof window === "undefined") {
    return "";
  }

  const appUrl = import.meta.env.VITE_APP_URL;
  const baseUrl = appUrl || window.location.origin;
  const redirectUrl = new URL("/auth/callback", baseUrl);

  const appRequested = options?.appRequested ?? isMobileApp();
  if (appRequested) {
    redirectUrl.searchParams.set("app", "1");
    redirectUrl.searchParams.set("source", "magiclink");
  }

  return redirectUrl.toString();
}

type RedirectToAppOptions = {
  forceDeepLink?: boolean;
};

/**
 * Attempts to redirect to the mobile app using deep linking.
 * Falls back to regular redirect if deep link fails.
 */
export function redirectToApp(path: string = "/dashboard", options?: RedirectToAppOptions): void {
  if (typeof window === "undefined") return;

  const deepLinkScheme = import.meta.env.VITE_APP_DEEP_LINK_SCHEME;
  const shouldDeepLink = options?.forceDeepLink ? Boolean(deepLinkScheme) : deepLinkScheme && isMobileApp();
  if (shouldDeepLink && deepLinkScheme) {
    const deepLinkUrl = `${deepLinkScheme}://${path.replace(/^\//, "")}`;
    const timeout = setTimeout(() => {
      window.location.href = path;
    }, 1000);

    window.location.href = deepLinkUrl;
    window.addEventListener("beforeunload", () => {
      clearTimeout(timeout);
    });
    return;
  }

  window.location.href = path;
}
